import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import {
  useGetAllLocationsQuery,
  useGetAllUsersQuery,
  useGetBroadcastPreviewQuery,
  useGetPlacesQuery,
  useSendBroadcastMutation,
} from '../../redux/adminApiSlice';

// Windows must match the backend AudienceWindow union.
const WINDOW_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Any time' },
  { value: 'hour', label: 'Last hour' },
  { value: 'today', label: 'Today (since 5am)' },
  { value: 'day', label: 'Last 24 hours' },
  { value: 'week', label: 'Last 7 days' },
  { value: 'month', label: 'Last 30 days' },
];

// Deep-link destinations — mirror of backend broadcastDeepLink.ts. `needsId`
// destinations require picking an entity (post/place/location/user).
type DestKind = 'none' | 'post' | 'place' | 'location' | 'user';
interface DestOption {
  value: string;
  label: string;
  needs: DestKind;
}
const DEST_OPTIONS: DestOption[] = [
  { value: 'alerts', label: 'Alerts tab (no deep link)', needs: 'none' },
  { value: 'here', label: 'Here feed', needs: 'none' },
  { value: 'nearby', label: 'Nearby', needs: 'none' },
  { value: 'messages', label: 'Messages inbox', needs: 'none' },
  { value: 'settings', label: 'Settings', needs: 'none' },
  { value: 'editProfile', label: 'Edit Profile', needs: 'none' },
  { value: 'addFavorites', label: 'Edit Profile → Add Favorites', needs: 'none' },
  { value: 'addNowPlaying', label: 'Edit Profile → Add Right Now', needs: 'none' },
  { value: 'ownProfile', label: 'Own profile', needs: 'none' },
  { value: 'friendRequests', label: 'Friend requests', needs: 'none' },
  { value: 'newPost', label: 'New post composer', needs: 'none' },
  { value: 'place', label: 'A specific Place…', needs: 'place' },
  { value: 'location', label: 'A specific Location…', needs: 'location' },
  { value: 'user', label: 'A specific User profile…', needs: 'user' },
  { value: 'post', label: 'A specific Post (paste id)…', needs: 'post' },
];

interface PickerOption {
  id: string;
  label: string;
}

const BroadcastPanel: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [title, setTitle] = useState('Grid');
  const [message, setMessage] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  // Audience filter (all axes optional, combine with AND on the backend).
  const [activeWithin, setActiveWithin] = useState('');
  const [signedUpWithin, setSignedUpWithin] = useState('');
  const [pushableOnly, setPushableOnly] = useState(false);
  const [includeExcluded, setIncludeExcluded] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<PickerOption[]>([]);
  const [locationFilter, setLocationFilter] = useState<{ id: string; level: string } | null>(null);

  // Deep link
  const [destination, setDestination] = useState('alerts');
  const [postId, setPostId] = useState('');
  const [placeTarget, setPlaceTarget] = useState<PickerOption | null>(null);
  const [locationTarget, setLocationTarget] = useState<PickerOption | null>(null);
  const [userTarget, setUserTarget] = useState<PickerOption | null>(null);

  const destOption = DEST_OPTIONS.find((d) => d.value === destination) || DEST_OPTIONS[0];

  // Reference data for pickers (full lists; filtered client-side by Autocomplete).
  const { data: allUsers } = useGetAllUsersQuery();
  const { data: allLocations } = useGetAllLocationsQuery();
  const { data: allPlaces } = useGetPlacesQuery();

  const userOptions: PickerOption[] = useMemo(
    () =>
      (allUsers || []).map((u) => ({
        id: u._id,
        label: `${u.displayName || u.username || 'Unknown'}${u.username ? ` (@${u.username})` : ''}`,
      })),
    [allUsers]
  );
  const placeOptions: PickerOption[] = useMemo(
    () => (allPlaces || []).map((p) => ({ id: p._id, label: p.name || 'Unnamed place' })),
    [allPlaces]
  );
  const locationOptions: PickerOption[] = useMemo(
    () =>
      (allLocations || []).map((l) => ({
        id: l._id,
        label: `${l.name || 'Unnamed'} · ${String(l.level).toLowerCase()}`,
      })),
    [allLocations]
  );
  // For the AUDIENCE location filter we need the level to send the right field.
  const audienceLocationOptions = useMemo(
    () => (allLocations || []).map((l) => ({ id: l._id, label: `${l.name} · ${String(l.level).toLowerCase()}`, level: String(l.level) })),
    [allLocations]
  );

  const previewParams = useMemo(() => {
    const p: Record<string, string | undefined> = {};
    if (activeWithin) p.activeWithin = activeWithin;
    if (signedUpWithin) p.signedUpWithin = signedUpWithin;
    if (pushableOnly) p.pushableOnly = 'true';
    if (includeExcluded) p.includeExcluded = 'true';
    if (selectedUsers.length) p.userIds = selectedUsers.map((u) => u.id).join(',');
    if (locationFilter) {
      const lvl = locationFilter.level.toUpperCase();
      if (lvl === 'NEIGHBORHOOD') p.neighborhoodId = locationFilter.id;
      else if (lvl === 'CITY') p.cityId = locationFilter.id;
      else if (lvl === 'STATE') p.stateId = locationFilter.id;
    }
    return p;
  }, [activeWithin, signedUpWithin, pushableOnly, includeExcluded, selectedUsers, locationFilter]);

  const {
    data: preview,
    isFetching: previewLoading,
    refetch,
  } = useGetBroadcastPreviewQuery(previewParams);
  const [sendBroadcast, sendState] = useSendBroadcastMutation();

  const recipientCount = preview?.recipientCount ?? 0;

  const isTargeted = !!(
    activeWithin ||
    signedUpWithin ||
    pushableOnly ||
    selectedUsers.length ||
    locationFilter
  );

  // Resolve the deep-link entity id for the currently chosen destination.
  const deepLinkId = useMemo(() => {
    switch (destOption.needs) {
      case 'post':
        return postId.trim() || undefined;
      case 'place':
        return placeTarget?.id;
      case 'location':
        return locationTarget?.id;
      case 'user':
        return userTarget?.id;
      default:
        return undefined;
    }
  }, [destOption.needs, postId, placeTarget, locationTarget, userTarget]);

  const deepLinkMissingId = destOption.needs !== 'none' && !deepLinkId;

  const audienceDescription = useMemo(() => {
    const parts: string[] = [];
    if (selectedUsers.length) parts.push(`${selectedUsers.length} hand-picked user${selectedUsers.length === 1 ? '' : 's'}`);
    if (locationFilter) parts.push(`in ${audienceLocationOptions.find((l) => l.id === locationFilter.id)?.label || 'a location'}`);
    if (activeWithin) {
      parts.push(`active ${WINDOW_OPTIONS.find((w) => w.value === activeWithin)?.label.toLowerCase()}`);
    }
    if (signedUpWithin) {
      parts.push(`signed up ${WINDOW_OPTIONS.find((w) => w.value === signedUpWithin)?.label.toLowerCase()}`);
    }
    if (pushableOnly) parts.push('with push enabled');
    if (parts.length === 0) return 'all users';
    return parts.join(' • ');
  }, [activeWithin, signedUpWithin, pushableOnly, selectedUsers, locationFilter, audienceLocationOptions]);

  const destDescription = useMemo(() => {
    if (destination === 'alerts') return 'Opens the Alerts tab (default).';
    const label = destOption.label.replace(/…$/, '');
    return `Tapping the notification opens: ${label}.`;
  }, [destination, destOption]);

  const onConfirmSend = async () => {
    setStatus(null);
    try {
      const result = await sendBroadcast({
        title: title.trim() || 'Grid',
        message: message.trim(),
        activeWithin: activeWithin || undefined,
        signedUpWithin: signedUpWithin || undefined,
        pushableOnly: pushableOnly || undefined,
        includeExcluded: includeExcluded || undefined,
        userIds: selectedUsers.length ? selectedUsers.map((u) => u.id) : undefined,
        neighborhoodId: previewParams.neighborhoodId,
        cityId: previewParams.cityId,
        stateId: previewParams.stateId,
        deepLink: destination !== 'alerts' ? destination : undefined,
        deepLinkId,
      }).unwrap();
      setConfirmOpen(false);
      setStatus(
        `Broadcast finished — attempted ${result.attempted}, sent ${result.sent}, failed ${result.failed}.`
      );
      void refetch();
    } catch (err: unknown) {
      const e = err as { data?: { message?: string }; message?: string };
      setStatus(e?.data?.message || e?.message || 'Broadcast failed');
      setConfirmOpen(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ width: '100%', maxWidth: 720 }}>
      <Typography variant="h6">Broadcast system notification</Typography>
      <Typography color="text.secondary">
        Sends an Alerts / push notification titled like a Grid system message. Target an audience
        below (hand-picked users, a location, and/or recency), and optionally set where a tap
        navigates. This is not a DM.
      </Typography>

      {/* ---------------- Audience ---------------- */}
      <Typography variant="subtitle2" sx={{ mt: 1 }}>
        Audience
      </Typography>

      <Autocomplete
        multiple
        size="small"
        options={userOptions}
        value={selectedUsers}
        onChange={(_e, val) => setSelectedUsers(val)}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        getOptionLabel={(o) => o.label}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Specific users (optional)"
            placeholder="Search name or @username"
          />
        )}
      />

      <Autocomplete
        size="small"
        options={audienceLocationOptions}
        value={audienceLocationOptions.find((l) => l.id === locationFilter?.id) || null}
        onChange={(_e, val) => setLocationFilter(val ? { id: val.id, level: val.level } : null)}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        getOptionLabel={(o) => o.label}
        renderInput={(params) => (
          <TextField {...params} label="Users in a location (optional)" placeholder="Search neighborhood / city / state" />
        )}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <FormControl fullWidth size="small">
          <InputLabel>Active within</InputLabel>
          <Select label="Active within" value={activeWithin} onChange={(e) => setActiveWithin(e.target.value)}>
            {WINDOW_OPTIONS.map((w) => (
              <MenuItem key={w.value} value={w.value}>
                {w.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <InputLabel>Signed up within</InputLabel>
          <Select label="Signed up within" value={signedUpWithin} onChange={(e) => setSignedUpWithin(e.target.value)}>
            {WINDOW_OPTIONS.map((w) => (
              <MenuItem key={w.value} value={w.value}>
                {w.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
        <FormControlLabel
          control={<Checkbox checked={pushableOnly} onChange={(e) => setPushableOnly(e.target.checked)} />}
          label="Only users with push enabled"
        />
        <FormControlLabel
          control={<Checkbox checked={includeExcluded} onChange={(e) => setIncludeExcluded(e.target.checked)} />}
          label="Include team / test / mock accounts"
        />
      </Stack>

      {previewLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Counting recipients…
          </Typography>
        </Box>
      ) : (
        <Alert severity={isTargeted ? 'info' : 'warning'}>
          This will notify approximately <strong>{recipientCount.toLocaleString()}</strong>{' '}
          {isTargeted ? <>users ({audienceDescription}).</> : <>users (everyone).</>}
        </Alert>
      )}

      <Divider />

      {/* ---------------- Deep link ---------------- */}
      <Typography variant="subtitle2">Deep link (where a tap goes)</Typography>
      <FormControl fullWidth size="small">
        <InputLabel>Destination</InputLabel>
        <Select label="Destination" value={destination} onChange={(e) => setDestination(e.target.value)}>
          {DEST_OPTIONS.map((d) => (
            <MenuItem key={d.value} value={d.value}>
              {d.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {destOption.needs === 'place' && (
        <Autocomplete
          size="small"
          options={placeOptions}
          value={placeTarget}
          onChange={(_e, val) => setPlaceTarget(val)}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          getOptionLabel={(o) => o.label}
          renderInput={(params) => <TextField {...params} label="Pick a place" placeholder="Search places" />}
        />
      )}
      {destOption.needs === 'location' && (
        <Autocomplete
          size="small"
          options={locationOptions}
          value={locationTarget}
          onChange={(_e, val) => setLocationTarget(val)}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          getOptionLabel={(o) => o.label}
          renderInput={(params) => <TextField {...params} label="Pick a location" placeholder="Search locations" />}
        />
      )}
      {destOption.needs === 'user' && (
        <Autocomplete
          size="small"
          options={userOptions}
          value={userTarget}
          onChange={(_e, val) => setUserTarget(val)}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          getOptionLabel={(o) => o.label}
          renderInput={(params) => <TextField {...params} label="Pick a user" placeholder="Search name or @username" />}
        />
      )}
      {destOption.needs === 'post' && (
        <TextField
          size="small"
          label="Post id"
          value={postId}
          onChange={(e) => setPostId(e.target.value)}
          placeholder="24-char post ObjectId"
          helperText="Posts are ephemeral — paste the post's id"
          fullWidth
        />
      )}
      <Typography variant="caption" color="text.secondary">
        {destDescription}
      </Typography>

      <Divider />

      <TextField
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        helperText='Shown as the notification title (default "Grid")'
        fullWidth
      />

      <TextField
        label="Message"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        multiline
        minRows={4}
        fullWidth
      />

      <Box>
        <Button
          variant="contained"
          color="warning"
          fullWidth={isMobile}
          disabled={!message.trim() || sendState.isLoading || recipientCount === 0 || deepLinkMissingId}
          onClick={() => setConfirmOpen(true)}
        >
          {sendState.isLoading
            ? 'Sending…'
            : deepLinkMissingId
            ? 'Pick a deep-link target'
            : `Send to ${recipientCount.toLocaleString()} ${isTargeted ? 'targeted ' : ''}user${
                recipientCount === 1 ? '' : 's'
              }`}
        </Button>
      </Box>

      {status ? <Alert severity="info">{status}</Alert> : null}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} fullScreen={isMobile} fullWidth>
        <DialogTitle>Send broadcast?</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Send to ~{recipientCount.toLocaleString()} users ({audienceDescription})?
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {destDescription}
          </Typography>
          <Typography variant="subtitle2">{title.trim() || 'Grid'}</Typography>
          <Typography color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {message.trim()}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => void onConfirmSend()}
            disabled={sendState.isLoading}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default BroadcastPanel;
